// src/components/MapboxGL/controls/BoundaryControl.js
import React from 'react'
import { Provider } from 'react-redux'
import { IconButton, Badge } from '@mui/material'
import { BorderOuter, BorderClear } from '@mui/icons-material'
import BaseMapControl from './BaseMapControl'

/**
 * Control de límites que se integra nativamente con Mapbox
 * - Botón único para mostrar/ocultar límites del contexto actual
 * - Modo tenant/cantón según estado externo
 * - Badge activo cuando el límite actual está visible
 */
// Colores por defecto para los boundaries
const BOUNDARY_COLORS = {
  canton: '#f34b21', // Rojo para cantón
  tenant: '#1976d2', // Azul para tenant
}

class BoundaryControl extends BaseMapControl {
  constructor(store, theme, onToggle, initialVisible = false) {
    super({ className: 'boundary-control' })
    this.store = store
    this.theme = theme
    this.onToggle = onToggle
    this.visible = Boolean(initialVisible)
    this.mode = 'canton'
    this.boundaryColor = BOUNDARY_COLORS.canton
  }

  /**
   * Actualiza el estado del control desde el exterior.
   * @param {Object} state
   * @param {boolean} state.visible
   * @param {'tenant' | 'canton'} state.mode
   */
  setControlState(state = {}) {
    this.visible = Boolean(state.visible)
    this.mode = state.mode === 'tenant' ? 'tenant' : 'canton'
    this.boundaryColor =
      this.mode === 'tenant' ? BOUNDARY_COLORS.tenant : BOUNDARY_COLORS.canton
    this._renderContent()
  }

  /**
   * Actualiza callback de toggle para evitar stale closures.
   * @param {(visible: boolean) => void} onToggle
   */
  updateOnToggle(onToggle) {
    this.onToggle = onToggle
    this._renderContent()
  }

  _renderContent() {
    const isTenantMode = this.mode === 'tenant'
    const isVisible = this.visible
    const boundaryColor = this.boundaryColor

    const tooltipTitle = isTenantMode
      ? isVisible
        ? 'Ocultar límite del tenant'
        : 'Mostrar límite del tenant'
      : isVisible
        ? 'Ocultar límite del cantón'
        : 'Mostrar límite del cantón'

    const self = this
    const boundaryTheme = this.theme
    this._root?.render(
      <Provider store={this.store}>
        <BoundaryButton
          tooltipTitle={tooltipTitle}
          isVisible={isVisible}
          boundaryColor={boundaryColor}
          theme={boundaryTheme}
          onToggle={nextVisible => {
            self.visible = nextVisible
            self.onToggle?.(nextVisible)
          }}
        />
      </Provider>,
    )
  }
}

const BoundaryButton = ({ tooltipTitle, isVisible, boundaryColor, theme, onToggle }) => {
  const [isHovered, setIsHovered] = React.useState(false)

  const handleClick = () => {
    onToggle(!isVisible)
  }

  const buttonStyles = {
    minWidth: '30px',
    width: '30px',
    height: '30px',
    padding: 0,
    margin: 0,
    color: theme.palette.text.primary,
    bgcolor:
      isVisible || isHovered
        ? theme.palette.secondary.main
        : theme.palette.background.paper,
    border: isVisible ? `2px solid ${boundaryColor}` : 'none',
    '&:hover': {
      bgcolor: theme.palette.secondary.main,
      color: theme.palette.primary.main,
    },
  }

  return (
    <IconButton
      onClick={handleClick}
      size="small"
      sx={buttonStyles}
      title={tooltipTitle}
      aria-label={tooltipTitle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      {isVisible ? (
        <BorderOuter fontSize="small" sx={{ color: boundaryColor }} />
      ) : (
        <BorderClear fontSize="small" />
      )}
    </IconButton>
  )
}

export default BoundaryControl
